export type CompanyProfile = {
  companyName: string;
  servicePhone: string;
  servicePhoneNote: string | null;
  serviceHours: string;
  serviceEmail: string;
  addressCity: string;
  addressDistrict: string;
  addressPostalCode: string | null;
  addressLine: string;
  contactPhone: string | null;
  smsPhone: string | null;
  copyright: string | null;
  lineId: string | null;
  lineUrl: string | null;
  facebookUrl: string | null;
  instagramUrl: string | null;
  youtubeUrl: string | null;
  logoUrl?: string | null;
  faviconUrl?: string | null;
  bookingPublicLookaheadDays?: number;
  isMaintenanceMode?: boolean;
  maintenanceMessage?: string | null;
};

type ApiCompanyProfile = {
  CompanyName: string;
  ServicePhone: string;
  ServicePhoneNote?: string | null;
  ServiceHours: string;
  ServiceEmail: string;
  AddressCity?: string;
  AddressDistrict?: string;
  AddressPostalCode?: string | null;
  AddressLine?: string;
  ContactPhone?: string | null;
  SmsPhone?: string | null;
  Copyright?: string | null;
  LineId?: string | null;
  LineUrl?: string | null;
  FacebookUrl?: string | null;
  InstagramUrl?: string | null;
  YoutubeUrl?: string | null;
  LogoUrl?: string | null;
  FaviconUrl?: string | null;
  BookingPublicLookaheadDays?: number;
  IsMaintenanceMode?: boolean;
  MaintenanceMessage?: string | null;
};

type ApiResponse<T> = T & { ok: boolean; message?: string };

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = (await response.json().catch(() => ({}))) as ApiResponse<T>;
  if (!response.ok || data.ok === false) {
    throw new Error(data.message || "資料載入失敗，請稍後再試。");
  }
  return data as T;
}

function normalize(item: ApiCompanyProfile): CompanyProfile {
  return {
    companyName: item.CompanyName || "",
    servicePhone: item.ServicePhone || "",
    servicePhoneNote: item.ServicePhoneNote || null,
    serviceHours: item.ServiceHours || "",
    serviceEmail: item.ServiceEmail || "",
    addressCity: item.AddressCity || "",
    addressDistrict: item.AddressDistrict || "",
    addressPostalCode: item.AddressPostalCode || null,
    addressLine: item.AddressLine || "",
    contactPhone: item.ContactPhone || null,
    smsPhone: item.SmsPhone || null,
    copyright: item.Copyright || null,
    lineId: item.LineId || null,
    lineUrl: item.LineUrl || null,
    facebookUrl: item.FacebookUrl || null,
    instagramUrl: item.InstagramUrl || null,
    youtubeUrl: item.YoutubeUrl || null,
    logoUrl: item.LogoUrl || null,
    faviconUrl: item.FaviconUrl || null,
    bookingPublicLookaheadDays: item.BookingPublicLookaheadDays || 180,
    isMaintenanceMode: item.IsMaintenanceMode ?? false,
    maintenanceMessage: item.MaintenanceMessage || null,
  };
}

/**
 * 供前台 Footer、聯絡我們等頁面取得公司基本資料（客服專線、Email、LINE、社群連結等）。
 * 若 API 尚未部署或失敗，回傳 null 讓呼叫端可以退回既有的預設文字，而不是顯示錯誤畫面。
 */
export async function fetchPublicCompanyProfile(): Promise<CompanyProfile | null> {
  try {
    const data = await fetch("/api/company-profile", {
      credentials: "include",
      headers: { Accept: "application/json" },
    }).then((response) => parseJsonResponse<{ ok: boolean; CompanyProfile: ApiCompanyProfile }>(response));
    return normalize(data.CompanyProfile);
  } catch {
    return null;
  }
}
